import creditorServiceColumn from "@/components/shared/cells/creditor-service-cell";
import { CurrencyCell } from "@/components/shared/cells/currency";
import { SelectCell } from "@/components/shared/cells/select";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Button } from "@/components/ui/button";
import { InvoiceDateCell } from "@/features/invoice-overview/cells/invoice-date";
import { InvoiceDateHeader } from "@/features/invoice-overview/headers/invoice-date";
import { CostCentersCell } from "@/features/invoices-table/cells/cost-centers";
import { CreatedAtCell } from "@/features/invoices-table/cells/created-at";
import { ExecutionDateCell } from "@/features/invoices-table/cells/execution-date";
import { TotalAmountCell } from "@/features/invoices-table/cells/total-amount";
import invoiceNoColumn from "@/features/invoices-table/columns/invoice-number-column";
import paymentDeadlineColumn from "@/features/invoices-table/columns/payment-deadline-column";
import { usePermissions } from "@/hooks/usePermissions";
import { useTenant } from "@/providers/session";
import type { GetAllInvoiceInvoice } from "@/queries/invoices/get-all";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { isWithinInterval } from "date-fns";
import { SendHorizonal } from "lucide-react";
import { useTranslation } from "react-i18next";

const selectColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => (
    <div className="flex items-center justify-center h-10 w-full">
      <SelectCell {...cell} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

const createdAtColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "createdAt",
  size: 130,
  accessorKey: "invoice.createdAt",
  filterFn: (row, columnId, value) => {
    if (!value) return true;

    return isWithinInterval(new Date(row.original.invoice.createdAt), {
      start: value.from,
      end: value.to,
    });
  },
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader column={header.column} title={t("created_at")} />
    );
  },
  cell: (cell) => {
    return <CreatedAtCell createdAt={cell.row.original.invoice.createdAt} />;
  },
};

const totalAmountColumn: ColumnDef<GetAllInvoiceInvoice> = {
  accessorKey: "invoice.totalAmount",
  id: "totalAmount",
  size: 100,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("invoice_total_amount")}
      />
    );
  },
  cell: (cell) => {
    return <TotalAmountCell invoice={cell.row.original.invoice} />;
  },
};

const paymentAmountToPayColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "paymentAmountToPay",
  accessorKey: "invoice.paymentAmountToPay",
  size: 150,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("invoice_payment_amount")}
      />
    );
  },
  cell: (cell) => {
    return (
      <CurrencyCell
        value={cell.row.original.invoice.paymentAmountToPay}
        currency={cell.row.original.invoice.currency}
      />
    );
  },
};

const costCentersColumn: ColumnDef<GetAllInvoiceInvoice> = {
  accessorKey: "costCenters",
  id: "costCenters",
  size: 200,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("cost_centers.column")}
      />
    );
  },
  cell: (cell) => {
    return (
      <CostCentersCell
        invoice={cell.row.original.invoice}
        lineItems={cell.row.original.lineItems}
      />
    );
  },
};

const executionDateColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "executionDate",
  accessorKey: "invoice.executionDate",
  size: 150,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("execution_date")}
      />
    );
  },
  cell: (cell) => {
    return <ExecutionDateCell {...cell} />;
  },
};

const actionsColumn: ColumnDef<GetAllInvoiceInvoice> = {
  id: "actions",
  size: 164,
  header: (header) => {
    return <DataTableColumnHeader column={header.column} title={"Aktionen"} />;
  },
  cell: (cell) => {
    const { companySlug } = useTenant();
    const navigate = useNavigate();

    function handleClick() {
      const invoiceId = cell.row.original.invoiceId;
      if (!companySlug || !invoiceId) return;

      navigate({
        to: "/$company/unternehmen/bezahlen",
        params: { company: companySlug },
        search: {
          invoiceIds: [invoiceId],
          navigateBackTo: "ueberweisungen",
        },
      });
    }

    return (
      <Button
        size="sm"
        className="bg-gray-100 text-gray-800 hover:bg-gray-300 active:bg-gray-300 focus:bg-gray-300 border border-gray-300"
        onClick={handleClick}
      >
        <SendHorizonal className="w-4 h-4 mr-2" />
        Überweisen
      </Button>
    );
  },
  enableSorting: false,
  enableHiding: false,
};

const invoiceDateColumn: (
  showConfidence: boolean,
) => ColumnDef<GetAllInvoiceInvoice> = (showConfidence) => ({
  id: "invoiceDate",
  accessorKey: "invoice.invoiceDate",
  size: 120,
  header: (header) => <InvoiceDateHeader {...header} />,
  sortingFn: (a, b) => {
    const invoiceDateA = a.original?.invoice.invoiceDate;
    const invoiceDateB = b.original?.invoice.invoiceDate;
    // Convert date strings to Date objects, using a large date for undefined/null
    const dateA = invoiceDateA
      ? new Date(invoiceDateA)
      : new Date(8640000000000000);
    const dateB = invoiceDateB
      ? new Date(invoiceDateB)
      : new Date(8640000000000000);

    // Compare the dates
    return dateA.getTime() - dateB.getTime();
  },
  cell: (cell) => {
    const { isSuperAdmin } = usePermissions();
    const { isOcrReviewRequired } = useTenant();

    if (
      isOcrReviewRequired &&
      !cell.row.original.invoice.ocrReviewed &&
      !isSuperAdmin
    )
      return null;
    return (
      <InvoiceDateCell
        invoice={cell.row.original.invoice}
        showConfidence={showConfidence}
      />
    );
  },
});

export function getColumnsForToPay(
  hasCostCenters: boolean,
): ColumnDef<GetAllInvoiceInvoice>[] {
  return [
    selectColumn,
    creditorServiceColumn<GetAllInvoiceInvoice>(false),
    totalAmountColumn,
    paymentAmountToPayColumn,
    paymentDeadlineColumn<GetAllInvoiceInvoice>(),
    executionDateColumn,
    invoiceDateColumn(false),
    invoiceNoColumn<GetAllInvoiceInvoice>(),
    ...(hasCostCenters ? [costCentersColumn] : []),
    createdAtColumn,
    actionsColumn,
  ];
}

export function getColumnsForPaid(
  hasCostCenters: boolean,
): ColumnDef<GetAllInvoiceInvoice>[] {
  return [
    selectColumn,
    creditorServiceColumn<GetAllInvoiceInvoice>(false),
    totalAmountColumn,
    invoiceDateColumn(false),
    invoiceNoColumn<GetAllInvoiceInvoice>(),
    ...(hasCostCenters ? [costCentersColumn] : []),
    createdAtColumn,
  ];
}
